<template>
    <v-dialog v-model="dialog" persistent max-width="1000px">
        <template v-slot:activator="{ on }">
            <v-btn v-if="smallButton" icon text v-on="on">
                <v-icon>fa-edit</v-icon>
            </v-btn>

            <v-btn v-else color="info" dark v-on="on" class="mb-2">
                <v-icon class="pr-1">fa-plus-circle</v-icon>
                Add New
            </v-btn>
        </template>
        <v-card>
            <v-card-title>
                <span class="text-h5">Product Mapping</span>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-form v-model="isFormValid" ref="form">
                      <v-row>
                          <v-col cols="2">
                            <h3>Account Type</h3>
                          </v-col>
                          <v-col cols="2">
                            <h3>Core Account Type</h3>
                          </v-col>
                          <v-col cols="2">
                            <h3>Core Product Code</h3>
                          </v-col>
                          <v-col cols="2">
                            <h3>Product Type</h3>
                          </v-col>
                          <v-col cols="2">
                            <h3 ref="kasasaBrandedHeader">{{workingMapping.accountType === 'LOAN' ? "Take Back Loan" : "Kasasa Branded"}}</h3>
                          </v-col>
                          <v-col cols="2">
                            <h3>FI Product Name</h3>
                          </v-col>
                      </v-row>
                      <v-row class="mt-5" align="start">
                          <v-col cols="2">
                            <v-select class="mr-2" ref="accountTypeSelect" outlined dense :items="['DDA','SAV','CD','LOAN','MMDA']" v-model="workingMapping.accountType" :rules="requiredRule"/>
                          </v-col>
                          <v-col cols="2">
                            <div v-for="(coreAccountProduct, index) in workingMapping.coreAccountProductTypes" v-bind:key="coreAccountProduct.id">
                              <v-select class="mr-2" outlined dense :items="selectableCoreAccountTypes" v-model="coreAccountProduct.coreAccountType" :rules="requiredRule"/>
                              <v-btn v-if="index === workingMapping.coreAccountProductTypes.length-1" id="addNewCodeButton" text color="primary" @click="addNewCode"><v-icon class="pr-1">fa-plus-circle</v-icon>Add new code</v-btn>
                            </div>
                          </v-col>
                          <v-col cols="2">
                            <div v-for="(coreAccountProduct, index) in workingMapping.coreAccountProductTypes" v-bind:key="index">
                              <v-text-field class="mr-2" dense outlined maxlength="50" v-model="coreAccountProduct.coreProductCode" :append-icon="workingMapping.coreAccountProductTypes.length > 1 ? 'fa-times' : ''" @click:append="removeCode(index)" :rules="requiredRule"/>
                            </div>
                          </v-col>
                          <v-col cols="2">
                            <v-select class="mr-2" outlined dense @change="onProductTypeChange" :items="selectableProductTypes" item-value="type" item-text="displayName" v-model="workingMapping.productType" :rules="requiredRule"/>

                            <template v-if="productSubTypeItems.length > 0">
                              <v-select ref="subTypeSelect" class="mr-2" outlined dense :items="productSubTypeItems" v-model="workingMapping.productSubType" label="Subtype" :rules="requiredRule"/>
                              <v-select ref="classificationSelect" class="mr-2" outlined dense :items="classificationItems" v-model="workingMapping.productClassification" label="Classification"/>
                              <v-select ref="acquisitionSelect" class="mr-2" outlined dense :items="acquisitionSourceItems" v-model="workingMapping.productAcquisitionSource" label="Acquisition Source"/>
                            </template>
                          </v-col>
                          <v-col cols="2" class="text-center">
                              <v-simple-checkbox id="kasasaBrandedCheck" class="mr-2" :ripple="false" @click="onKasasaBrandedChange" v-model = "workingMapping.kasasaBranded" :disabled="!this.selectedProductType || (!this.selectedProductType.kasasaPowered && this.workingMapping.accountType !== 'LOAN')"/>
                          </v-col>
                          <v-col cols="2">
                            <v-text-field dense outlined name="fiProductName" v-model="workingMapping.fiProductName" :rules="requiredAndAsciiRule"/>
                          </v-col>
                      </v-row>
                    </v-form>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn large @click="cancel" :disabled="saving">Cancel</v-btn>
                <v-btn id="saveButton" large class="blue white--text" @click="save"
                  :loading="saving" :disabled="!isFormValid">
                        Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import { ProductMappingResource, ProductMapping, CoreAccountProduct }
  from '@/api/productmapping/';
import { ProductTypeMap, ProductTypeDetails, NamePair }
  from '@/api/producttypes/';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import { AccountProductTypeMap } from '@/api/accountproducttypes';

@Component({ computed: mapState(['fiId']) })
export default class ProductMappingDialog extends Vue {
    @Inject('ProductMappingResource')
    mappingResource!:ProductMappingResource;

    @Prop()
    mapping!:ProductMapping;

    @Prop()
    productTypeMap!:ProductTypeMap;

    @Prop()
    smallButton!:boolean;

    @Prop()
    coreAccountTypeMap!: Map<string, string[]>;

    @Prop()
    accountProductTypeMap!: AccountProductTypeMap;

    workingMapping:ProductMapping = ProductMappingDialog.createEmptyMapping();

    selectableCoreAccountTypes: string[] = [];

    selectableProductTypes: ProductTypeDetails[] = [];

    selectedProductType: ProductTypeDetails | null = null;

    fiId!:string;

    dialog:boolean = false;

    saving:boolean = false;

    isFormValid:boolean = false;

    productSubTypeItems: any[] = [];

    classificationItems: any[] = [];

    acquisitionSourceItems: any[] = [];

    initializeWorkingMapping() {
      if (this.mapping) {
        Object.assign(this.workingMapping, this.mapping);

        this.workingMapping.coreAccountProductTypes = [];
        for (let i = 0; i < this.mapping.coreAccountProductTypes.length; i++) {
          this.workingMapping.coreAccountProductTypes.push({
            id: null,
            coreAccountType: null,
            coreProductCode: null,
            linkedCoreAccountProduct: null,
            isCurrentlyLinked: false,
            lastQueryCount: null,
          });
          Object.assign(this.workingMapping.coreAccountProductTypes[i], this.mapping.coreAccountProductTypes[i]);
        }
      } else {
        this.workingMapping = ProductMappingDialog.createEmptyMapping();
      }

      this.selectedProductType = this.productTypeMap[this.workingMapping.productType!];
      this.initialiseSelectionLists();
    }

  isAsciiOnly = (str : string) => {
    for (let i = 0; i < str.length; i++) {
      if (str.charCodeAt(i) > 127) {
        return false;
      }
    }
    return true;
  }

  initialiseSelectionLists() {
    this.productSubTypeItems = [];
    this.classificationItems = [{ text: 'None', value: null }];
    this.acquisitionSourceItems = [{ text: 'None', value: null }];
    if (this.selectedProductType && this.selectedProductType.productSubTypes && this.selectedProductType.productSubTypes.length > 0) {
      this.selectedProductType.productSubTypes.forEach((subType:NamePair) => {
        this.productSubTypeItems.push({
          text: subType.displayName,
          value: subType.name,
        });
      });

      this.selectedProductType.productClassifications.forEach((classification:NamePair) => {
        this.classificationItems.push({
          text: classification.displayName,
          value: classification.name,
        });
      });

      this.selectedProductType.productAcquisitionSources.forEach((acquisitionSource:NamePair) => {
        this.acquisitionSourceItems.push({
          text: acquisitionSource.displayName,
          value: acquisitionSource.name,
        });
      });
    }
  }

  static createEmptyMapping() : ProductMapping {
    return {
      id: null,
      coreAccountProductTypes: [ProductMappingDialog.createEmptyCoreAccountProduct()],
      accountType: null,
      productType: null,
      kasasaBranded: false,
      kasasaPowered: false,
      fiProductName: '',
      hasAssociatedAccounts: false,
      externalProductMappingId: null,
      productSubType: null,
      productAcquisitionSource: null,
      productClassification: null,
    };
  }

  static createEmptyCoreAccountProduct() : CoreAccountProduct {
    return {
      id: null,
      coreAccountType: null,
      coreProductCode: null,
      linkedCoreAccountProduct: null,
      isCurrentlyLinked: false,
      lastQueryCount: 0,
    };
  }

  requiredRule:Array<any> = [(value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    return true;
  }];

  requiredAndAsciiRule:Array<any> = [(value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    if (!this.isAsciiOnly(value)) {
      return 'Value has non-ascii characters';
    }
    return true;
  }];

  cancel() {
    const form: any = this.$refs.form;
    form.reset();
    this.dialog = false;
  }

  save() {
    this.saving = true;
    this.mappingResource.save(this.fiId, this.workingMapping).then((savedMapping:ProductMapping) => {
      if (this.mapping) {
        Object.assign(this.mapping, savedMapping);
      }

      this.$emit('success', savedMapping);
      this.dialog = false;
    })
      .catch((error:any) => {
        this.showError();
      })
      .finally(() => {
        this.saving = false;
      });
  }

    @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.saving = false;

      this.productSubTypeItems = [];

      this.initializeWorkingMapping();
    }
  }

    @Watch('workingMapping.accountType')
    onAccountTypeChange() {
      if (this.coreAccountTypeMap.has(this.workingMapping.accountType!)) {
        this.selectableCoreAccountTypes = this.coreAccountTypeMap.get(this.workingMapping.accountType!)!;

        const productTypes:string[]|undefined = this.accountProductTypeMap[this.workingMapping.accountType!];
        this.selectableProductTypes = [];
        let currentValid = false;
        if (productTypes) {
          productTypes.forEach((productType:string) => {
            this.selectableProductTypes.push(this.productTypeMap[productType]);
            if (productType === this.workingMapping.productType) {
              currentValid = true;
            }
          });

          if (!currentValid) {
            this.workingMapping.productType = null;
          }
        }
      } else {
        this.selectableCoreAccountTypes = [];
        this.selectableProductTypes = [];
        this.workingMapping.productType = null;
      }
    }

    onProductTypeChange() {
      if (this.workingMapping.productType === null) {
        this.selectedProductType = null;
      }

      this.workingMapping.productSubType = null;
      this.workingMapping.productClassification = null;
      this.workingMapping.productAcquisitionSource = null;

      this.selectedProductType = this.productTypeMap[this.workingMapping.productType!];

      if (!this.selectedProductType || !this.selectedProductType.kasasaPowered) {
        this.workingMapping.kasasaBranded = false;
      } else if (this.workingMapping.kasasaBranded) {
        this.workingMapping.fiProductName = this.selectedProductType.displayName;
      }

      this.initialiseSelectionLists();
    }

    onKasasaBrandedChange() {
      if (this.workingMapping.accountType !== 'LOAN') {
        if (this.workingMapping.kasasaBranded) {
          this.workingMapping.fiProductName = this.selectedProductType!.displayName;
        } else {
          this.workingMapping.fiProductName = '';
        }
      }
    }

    addNewCode() {
      this.workingMapping.coreAccountProductTypes.push(ProductMappingDialog.createEmptyCoreAccountProduct());
    }

    removeCode(index: number) {
      this.workingMapping.coreAccountProductTypes.splice(index, 1);
    }

    showError() {
      const al = new Alert('An unexpected error occurred.', NoticeClass.ERROR);
      al.setTimeout(5000);
		  this.$store.dispatch('notices/add', al);
	  }
}
</script>

<style lang="scss" scoped>
</style>
